import { NormalizedError, ErrorContext } from './error_type';
import { AxiosError, AxiosResponse } from 'axios';
import { AuthState } from 'src/constants/auth';

export const MFA_SESSION_EXPIRED_MESSAGE = 'Your admin session has expired. Please log in again.';

export function parseApiError(response: AxiosResponse | undefined): NormalizedError {
  const status = response?.status;
  const data = response?.data;
  const timestamp = Date.now();

  if (!status) {
    return {
      code: 999,
      title: 'Network Error',
      description: 'Unable to connect to the server. Please check your internet connection.',
      context: 'network',
      recoverable: true,
      retryable: true,
      userActionable: true,
      timestamp
    };
  }

  // Auth errors
  if (status === 401) {
    return {
      code: 401,
      title: 'Authentication Required',
      description: 'Your session has expired. Please log in again.',
      context: 'auth',
      recoverable: true,
      retryable: false,
      userActionable: true,
      timestamp
    };
  }

  if (status === 403) {
    return {
      code: 403,
      title: 'Access Denied',
      description: "You don't have permission to perform this action.",
      context: 'auth',
      recoverable: false,
      retryable: false,
      userActionable: false,
      timestamp
    };
  }

  // Structured API error format (backend custom errors)
  if (data?.detail?.error_code && data?.detail?.error_title) {
    return {
      code: data.detail.error_code,
      title: data.detail.error_title,
      description: data.detail.error_description || 'An error occurred while processing your request.',
      context: determineContext(data.detail.error_code),
      recoverable: isRecoverable(data.detail.error_code),
      retryable: isRetryable(data.detail.error_code),
      userActionable: isUserActionable(data.detail.error_code),
      timestamp,
      originalError: data
    };
  }

  // Validation errors
  if (status === 422) {
    // Check if this is a file upload error (file size/content validation)
    const detail =
      typeof data?.detail === 'string'
        ? data.detail
        : Array.isArray(data?.detail)
          ? data.detail.map((err: any) => err.msg || err.message).join(', ')
          : 'Invalid input provided.';

    // Check if it's a file size/content error by looking for keywords
    const isFileUploadError =
      detail.toLowerCase().includes('file size') ||
      detail.toLowerCase().includes('exceeds maximum') ||
      detail.toLowerCase().includes('too large') ||
      detail.toLowerCase().includes('content length');

    return {
      code: 422,
      title: isFileUploadError ? 'File Too Large' : 'Validation Error',
      description: detail,
      context: 'form',
      recoverable: true,
      retryable: false,
      userActionable: true,
      timestamp,
      originalError: data
    };
  }

  // Not found errors
  if (status === 404) {
    return {
      code: 404,
      title: 'Resource Not Found',
      description: 'The requested resource could not be found.',
      context: 'network',
      recoverable: false,
      retryable: false,
      userActionable: false,
      timestamp
    };
  }

  // File too large
  if (status === 413) {
    const errorMessage = typeof data?.detail === 'string' ? data.detail : 'File is too large or content is too long.';
    return {
      code: 413,
      title: 'File Too Large',
      description: errorMessage,
      context: 'form',
      recoverable: true,
      retryable: false,
      userActionable: true,
      timestamp,
      originalError: data
    };
  }

  // Rate limiting
  if (status === 429) {
    return {
      code: 429,
      title: 'Too Many Requests',
      description: "You've made too many requests. Please wait a moment before trying again.",
      context: 'network',
      recoverable: true,
      retryable: true,
      userActionable: true,
      timestamp
    };
  }

  // Server errors
  if (status >= 500) {
    return {
      code: status,
      title: 'Server Error',
      description: 'Something went wrong on our end. Please try again later.',
      context: 'network',
      recoverable: true,
      retryable: true,
      userActionable: false,
      timestamp
    };
  }

  // Other 4xx errors
  return {
    code: status,
    title: 'Request Error',
    description: typeof data?.detail === 'string' ? data.detail : 'An error occurred with your request.',
    context: 'unknown',
    recoverable: false,
    retryable: false,
    userActionable: false,
    timestamp,
    originalError: data
  };
}

function determineContext(errorCode: number): ErrorContext {
  // Auth-related errors
  if (errorCode >= 4000 && errorCode < 4100) {
    return 'auth';
  }

  // Form/validation errors
  if (errorCode >= 4100 && errorCode < 4200) {
    return 'form';
  }

  // Network/infrastructure errors
  if (errorCode >= 5000 && errorCode < 5100) {
    return 'network';
  }

  return 'unknown';
}

function isRecoverable(errorCode: number): boolean {
  // Rate limits, temporary server issues, etc.
  const recoverableCodes = [429, 5001, 5002, 5005, 5006, 5007];
  return recoverableCodes.includes(errorCode);
}

function isRetryable(errorCode: number): boolean {
  // Network issues, server errors, rate limits
  const retryableCodes = [429, 500, 502, 503, 504, 5001, 5002, 5005, 5006, 5007];
  return retryableCodes.includes(errorCode);
}

function isUserActionable(errorCode: number): boolean {
  // Errors where user can take action (login, retry, etc.)
  const userActionableCodes = [401, 413, 422, 429, 5001, 5002];
  return userActionableCodes.includes(errorCode);
}

export function isNetworkError(error: any): boolean {
  return !error?.response && (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error'));
}

export function isTimeoutError(error: any): boolean {
  return error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
}

/**
 * Pulls a renderable string out of an API error's `detail` field.
 *
 * `detail` arrives in three shapes. `HTTPException` sends a plain string; the global
 * 500 handler in `main.py` sends an object (`error_code` / `error_title` /
 * `error_description`); FastAPI request validation sends an array of issues. Callers
 * typically hold `detail` in state typed `string`, but bind their catch as `any`, so an
 * object shape type-checks and then throws "Objects are not valid as a React child" when
 * rendered — the error surface itself breaks at the moment the user needs it. Anything
 * reading `data.detail` should funnel through here first.
 *
 * Returns undefined when there is no usable message, so callers can apply their own
 * context-specific fallback copy.
 */
export function extractErrorDetail(detail: unknown): string | undefined {
  if (typeof detail === 'string') {
    return detail.trim() || undefined;
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((issue) => (typeof issue === 'string' ? issue : extractErrorDetail(issue)))
      .filter((message): message is string => !!message);
    return messages.length ? messages.join(', ') : undefined;
  }

  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>;
    // error_description/error_title come from the global 500 handler, msg/message from
    // FastAPI validation issues.
    for (const key of ['error_description', 'error_title', 'msg', 'message', 'detail']) {
      const value = record[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }

  return undefined;
}

/**
 * Return the most specific message a failed API call carries: the backend's own `detail`
 * first, then the normalised description, which is generic for some statuses (403, 404).
 *
 * Returns undefined when there is neither, so callers can apply their own fallback copy.
 */
export function extractApiErrorMessage(error: unknown): string | undefined {
  const err = error as (Partial<AxiosError<{ detail?: unknown }>> & { parsed?: NormalizedError }) | undefined;
  const detail = extractErrorDetail(err?.response?.data?.detail);
  // The backend signals an expired admin MFA session with a machine-readable sentinel.
  if (detail === AuthState.MFA_REQUIRED) return MFA_SESSION_EXPIRED_MESSAGE;
  return detail ?? (err?.parsed?.description || undefined);
}
